"use client";

import { format } from "date-fns";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar, CalendarProps } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SelectSingleEventHandler } from "react-day-picker";
import { CalendarPlus } from "lucide-react";

type DatePickerProps = {
  label: string;
  onDateChange: SelectSingleEventHandler;
  className?: string;
  date?: Date;
  calendarProps?: CalendarProps;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  date,
  className,
  calendarProps,
  onDateChange,
}) => {
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    date
  );

  const dateChangeHandler: SelectSingleEventHandler = (newDate, ...params) => {
    setSelectedDate(newDate);
    onDateChange?.(newDate, ...params);
  };
  return (
    <Popover>
      <PopoverTrigger asChild className={cn("w-[177px] px-4 py-2", className)}>
        <Button
          variant={"secondary"}
          className={cn(
            "border border-shades-neutral-200 bg-white",
            selectedDate
              ? "text-text-secondary"
              : "text-uiColors-neutrals-blue-gray-light",
            "flex items-center justify-between"
          )}
          rightIcon={<CalendarPlus width={24} height={24} />}
          title={selectedDate ? format(selectedDate, "MMM d, yyyy") : label}
        />
      </PopoverTrigger>
      <PopoverContent className="mx-auto w-auto p-0">
        <Calendar
          {...calendarProps}
          mode="single"
          captionLayout="dropdown-buttons"
          selected={selectedDate}
          onSelect={dateChangeHandler}
          initialFocus
          className="flex gap-2"
          fromYear={1960}
          toYear={2050}
          disabled={(date) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Disable dates before today
            if (date < today) return true;

            // Disable dates based on fromDate/toDate constraints
            if (calendarProps?.fromDate && date < calendarProps.fromDate)
              return true;
            if (calendarProps?.toDate && date > calendarProps.toDate)
              return true;

            return false;
          }}
          classNames={{
            caption: "flex items-center justify-center px-2 py-1 gap-2",
            nav_button_previous: "absolute left-2 top-6",
            nav_button_next: "absolute right-2 top-6",
            caption_dropdowns: "flex items-center gap-2",
            caption_label: "hidden",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
